export interface StorageLike {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}
export interface LockManagerLike {
  request<T>(
    name: string,
    options: { ifAvailable: true },
    callback: (lock: unknown | null) => Promise<T> | T
  ): Promise<T>;
}
export interface LogoutOwnershipDependencies {
  delay?: (milliseconds: number) => Promise<void>;
  graceDelay?: (milliseconds: number) => Promise<void>;
  locks?: LockManagerLike;
  now?: () => number;
  randomToken?: () => string;
  storage?: StorageLike;
}
interface Lease {
  token: string;
  claimed: boolean;
  expiresAt: number;
}
interface OwnershipActions {
  onFollower: () => Promise<void> | void;
  onOwner: () => Promise<void> | void;
}
const CONTENTION_MS = 25;
const LEASE_MS = 5_000;
function parseLease(value: string | null): Lease | undefined {
  if (!value) return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return undefined;
    const lease = parsed as Record<string, unknown>;
    if (
      Object.keys(lease).length !== 3 ||
      typeof lease.token !== 'string' ||
      lease.token.length === 0 ||
      typeof lease.claimed !== 'boolean' ||
      typeof lease.expiresAt !== 'number' ||
      !Number.isFinite(lease.expiresAt)
    )
      return undefined;
    return { token: lease.token, claimed: lease.claimed, expiresAt: lease.expiresAt };
  } catch {
    return undefined;
  }
}
function defaultToken() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}
function defaultDelay(milliseconds: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
}
export async function runLogoutOwnership(
  lockName: string,
  leaseKey: string,
  dependencies: LogoutOwnershipDependencies,
  actions: OwnershipActions
): Promise<'owner' | 'follower'> {
  if (dependencies.locks)
    return dependencies.locks.request(lockName, { ifAvailable: true }, async (lock) => {
      if (lock === null) {
        await actions.onFollower();
        return 'follower';
      }
      try {
        await actions.onOwner();
        return 'owner';
      } finally {
        await (dependencies.graceDelay ?? defaultDelay)(LEASE_MS);
      }
    });
  const storage = dependencies.storage;
  if (!storage) {
    await actions.onFollower();
    return 'follower';
  }
  const now = dependencies.now ?? Date.now;
  const current = parseLease(storage.getItem(leaseKey));
  if (current?.claimed && current.expiresAt > now()) {
    await actions.onFollower();
    return 'follower';
  }
  const token = (dependencies.randomToken ?? defaultToken)();
  storage.setItem(leaseKey, JSON.stringify({ token, claimed: false, expiresAt: now() + LEASE_MS }));
  await (dependencies.delay ?? defaultDelay)(CONTENTION_MS);
  const candidate = parseLease(storage.getItem(leaseKey));
  if (candidate?.token !== token || candidate.claimed) {
    await actions.onFollower();
    return 'follower';
  }
  storage.setItem(leaseKey, JSON.stringify({ token, claimed: true, expiresAt: now() + LEASE_MS }));
  if (parseLease(storage.getItem(leaseKey))?.token !== token) {
    await actions.onFollower();
    return 'follower';
  }
  await actions.onOwner();
  return 'owner';
}
